import { useEffect, useState } from 'react';
import Input from '../Input/Input';
import './infocardsmall.css';
import AvatarImageCard from '../AvatarImageCard/AvatarImageCard';
import { DropdownItemRole, DropdownItemStatus, IUserBase, Role } from '../../types/type';
import DropDownInput from '../DropDownInput/DropDownInput';
import { instance } from '../../api/api';
import { fetchAdminUsers } from '../../store/admin/adminDataSlice';
import { useAppDispatch } from '../../store/hooks';
import PhoneInput from '../PhoneInput/PhoneInput';
import { NO_DATE, formatBackendDate } from '../../utils/date.utils';
import { pickFieldErrors, toApiError } from '../../utils/apiError';
import { FIELD_MESSAGES } from '../../utils/fieldMessages';
import { buildProfileUpdate } from './profileUpdate';
import { isCompletePhone, nationalDigits } from '../../utils/phone';

// Поля PUT /profiles/:id, ошибки по которым карточка показывает под своим input
const PROFILE_FIELDS = ['login', 'firstName', 'middleName', 'lastName', 'phoneNumber', 'email', 'address'] as const;

// registered — водитель завершил регистрацию: имя, фамилия, емейл и город у него обязательны
function InfoCardSmall({ user, registered = false }: { user: IUserBase; registered?: boolean }) {
  const isRegisteredDriver = user.role === 'driver' && registered;
  const [login, setLogin] = useState(user.username || '');

  const [firstName, setFirstName] = useState(user.firstName || '');
  const [middleName, setMiddleName] = useState(user.middleName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [email, setEmail] = useState(user.email || '');
  const [address, setAddress] = useState(user.address || '');
  const [, setRole] = useState('');
  const [password, setPassword] = useState('');
  const [editable, setEditable] = useState(false);
  const [resetPasswordMessage, setResetPasswordMessage] = useState('');
  const [resetPasswordStatus, setResetPasswordStatus] = useState<'success' | 'error' | ''>('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const dispatch = useAppDispatch();

  const checkErrorKeys = (obj: Record<string, string>) => {
    const keys = Object.keys(obj);

    for (const key of keys) {
      if (obj[key] !== '') {
        return false;
      }
    }
    return true;
  };

  const handleRole = () => {
    if (user.role === 'admin') {
      setRole('Админ');
    } else if (user.role === 'driver') {
      setRole('Водитель');
    } else if (user.role === 'medic') {
      setRole('Медик');
    } else if (user.role === 'technik') {
      setRole('Техник');
    }
  };

  const [errors, setErrors] = useState({
    login: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    address: '',
    password: '',
  });

  const validateLogin = (value: string) => {
    if (!value) return 'Логин обязателен.';

    if (value.length < 6) return 'Логин должен содержать минимум 6 символов.';

    const loginRegex = /^[a-zA-Z0-9]+$/;
    if (!loginRegex.test(value)) return 'Логин должен содержать только латинские буквы и цифры.';

    return '';
  };

  const validateFirstName = (value: string) => {
    if (!value.trim()) return FIELD_MESSAGES.required;
    return '';
  };

  const validateLastName = (value: string) => {
    if (!value.trim()) return FIELD_MESSAGES.required;
    return '';
  };

  const validatePhone = (value: string) => {
    if (!value) return FIELD_MESSAGES.required;
    return isCompletePhone(nationalDigits(value)) ? '' : FIELD_MESSAGES.invalid;
  };

  const validateEmail = (value: string) => {
    if (!value.trim()) return isRegisteredDriver ? FIELD_MESSAGES.required : '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim())) return FIELD_MESSAGES.invalidEmail;
    return '';
  };

  const validateAddress = (value: string) => {
    if (!value.trim() && isRegisteredDriver) return FIELD_MESSAGES.required;
    return '';
  };

  const validatePassword = (value: string) => {
    if (!value) return 'Пароль обязателен.';
    if (value.length < 8) return 'Пароль должен содержать минимум 8 символов.';
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    if (!passwordRegex.test(value)) return 'Пароль должен содержать как минимум одну букву и одну цифру.';
    return '';
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLogin(newValue);

    const error = validateLogin(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      login: error,
    }));
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFirstName(newValue);

    const error = validateFirstName(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      firstName: error,
    }));
  };

  const handleMiddleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setMiddleName(newValue);

    // Отчество необязательное, ошибок нет
    setErrors((prevErrors) => ({
      ...prevErrors,
      middleName: '',
    }));
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLastName(newValue);

    const error = validateLastName(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      lastName: error,
    }));
  };

  const handlePhoneChange = (numericValue: string) => {
    setPhone(numericValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      phoneNumber: validatePhone(numericValue),
    }));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEmail(newValue);

    const error = validateEmail(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      email: error,
    }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setAddress(newValue);

    const error = validateAddress(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      address: error,
    }));
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPassword(newValue);

    const error = validatePassword(newValue);
    setErrors((prevErrors) => ({
      ...prevErrors,
      password: error,
    }));
  };

  useEffect(handleRole, []);

  const [selectedRole, setSelectedRole] = useState<Role>(user.role);
  const [selectedStatus, setSelectedStatus] = useState<boolean>(user.isActive);

  const dropdownRole: DropdownItemRole[] = [
    { text: 'Водитель', value: 'driver' },
    { text: 'Техник', value: 'technik' },
    { text: 'Медик', value: 'medic' },
    { text: 'Админ', value: 'admin' },
  ];

  const dropdownStatus: DropdownItemStatus[] = [
    { text: 'Активен', value: true },
    { text: 'Не активен', value: false },
  ];

  const handleSelectRole = (value: Role) => {
    setSelectedRole(value);
  };

  const handleSelectStatus = (value: boolean) => {
    setSelectedStatus(value);
  };

  if (!user) {
    return <div>Пользователь не найден</div>;
  }

  // Валидаторы срабатывают только при вводе, поэтому перед сохранением проверяем все поля заново
  const validateForSave = () => {
    const next = {
      ...errors,
      firstName: validateFirstName(firstName),
      lastName: validateLastName(lastName),
      phoneNumber: validatePhone(phone),
      email: validateEmail(email),
      address: validateAddress(address),
    };
    setErrors(next);
    return checkErrorKeys(next);
  };

  const updateUser = async () => {
    const userData = buildProfileUpdate(
      {
        login,
        firstName,
        middleName,
        lastName,
        phone,
        email,
        address,
        password,
        role: selectedRole,
        status: selectedStatus,
      },
      user.role,
    );
    await instance.put(`/profiles/${user.id}`, userData);
    dispatch(fetchAdminUsers());
  };

  const handleSave = async () => {
    setSaveError('');
    if (!validateForSave()) return;

    setSaving(true);
    try {
      await updateUser();
      setEditable(false);
      setResetPasswordMessage(''); // Сбрасываем сообщение при сохранении
      setResetPasswordStatus('');
    } catch (err) {
      // Остаёмся в режиме редактирования: ошибки полей под полями, остальное
      // (например, 422 registration_incomplete) — текстом сервера над формой
      const apiError = toApiError(err, 'Не удалось сохранить профиль');
      const fieldErrors = pickFieldErrors(apiError, PROFILE_FIELDS);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
        return;
      }
      setSaveError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      setResetPasswordMessage('Отправка запроса...');
      setResetPasswordStatus('');

      // Отправляем запрос на сброс пароля
      const response = await instance.post(`/profile/${user.id}/reset-password`);

      // Если запрос успешный, показываем сообщение
      setResetPasswordMessage('Пароль сброшен. Новый пароль отправлен на email пользователя.');
      setResetPasswordStatus('success');

      // Через 3 секунды убираем сообщение
      setTimeout(() => {
        setResetPasswordMessage('');
        setResetPasswordStatus('');
      }, 3000);
    } catch (error) {
      setResetPasswordMessage('Ошибка при сбросе пароля. Попробуйте позже.');
      setResetPasswordStatus('error');

      setTimeout(() => {
        setResetPasswordMessage('');
        setResetPasswordStatus('');
      }, 3000);
    }
  };

  return (
    <div className={`profile__card  ${user.role === 'medic' ? 'profile__card__medic' : ''}`}>
      <AvatarImageCard user={user} />
      <div className="info__card">
        <div className="info__card-header">
          <div className="info__card-header__title">Контактная информация</div>
          <div
            className="info__card-header__descr"
            onClick={() => {
              if (saving) return;
              if (editable) {
                handleSave();
              } else {
                setEditable(true);
              }
            }}
          >
            {editable ? 'Сохранить' : 'Изменить'}
          </div>
        </div>
        {saveError && <div className="input__error info__card-error">{saveError}</div>}
        <div className={`info__card-body ${user.role === 'driver' ? 'Driver' : ''}`}>
          <Input
            label="Логин"
            id="login"
            placeholder="Введите ваше имя"
            value={login}
            onChange={handleLoginChange}
            required
            editable={editable}
            style={{ gridColumn: '1/2' }}
          />
          {errors.login && <span className="error">{errors.login}</span>}

          {/* Показываем поле пароля только если это не водитель */}
          {user.role !== 'driver' && (
            <>
              <Input
                type="password"
                label="Пароль"
                id="password"
                placeholder="Введите ваш пароль"
                value={password}
                onChange={handlePasswordChange}
                required
                editable={editable}
                style={{ gridColumn: '1/2' }}
              />
              {errors.password && <span className="error">{errors.password}</span>}
            </>
          )}

          <Input
            label="Фамилия"
            id="lastName"
            placeholder="Введите вашу фамилию"
            value={lastName}
            onChange={handleLastNameChange}
            required
            editable={editable}
            style={{ gridColumn: '1/2' }}
          />
          {errors.lastName && <span className="error">{errors.lastName}</span>}

          <Input
            label="Имя"
            id="firstName"
            placeholder="Введите ваше имя"
            value={firstName}
            onChange={handleFirstNameChange}
            required
            editable={editable}
            style={{ gridColumn: '1/2' }}
          />
          {errors.firstName && <span className="error">{errors.firstName}</span>}

          <Input
            label="Отчество"
            id="middleName"
            placeholder="Введите ваше отчество"
            value={middleName}
            onChange={handleMiddleNameChange}
            editable={editable}
            style={{ gridColumn: '1/2' }}
          />
          {errors.middleName && <span className="error">{errors.middleName}</span>}

          <PhoneInput
            label="Телефон"
            id="nickname"
            placeholder="Введите ваш телефон"
            value={phone}
            onChangeValue={handlePhoneChange}
            required
            editable={editable}
            style={{ gridColumn: '1/2' }}
          />
          {errors.phoneNumber && <span className="error">{errors.phoneNumber}</span>}

          <Input
            label="Емейл"
            id="email"
            placeholder="Введите ваш емейл"
            value={email}
            onChange={handleEmailChange}
            required
            editable={editable}
            style={{ gridColumn: '1/2' }}
          />
          {errors.email && <span className="error">{errors.email}</span>}

          {/* Поле "Город" для водителя */}
          {user.role === 'driver' && (
            <>
              <Input
                label="Город"
                id="address"
                placeholder="Введите город"
                value={address}
                onChange={handleAddressChange}
                required={isRegisteredDriver}
                editable={editable}
                style={{ gridColumn: '1/2' }}
              />
              {errors.address && <span className="error">{errors.address}</span>}
            </>
          )}

          <DropDownInput
            label="Роль"
            items={dropdownRole as DropdownItemRole[]}
            onSelect={handleSelectRole}
            initialValue={selectedRole}
            editable={editable && user.role !== 'driver'}
            style={{ gridColumn: '1/2' }}
          />
          <Input
            label="Дата регистрации"
            id="createdAt"
            value={formatBackendDate(user.registeredAt, NO_DATE)}
            editable={false}
            style={{ gridColumn: '1/2' }}
          />

          {/* loginDate — null, если пользователь ни разу не входил */}
          <Input
            label="Дата последней активности"
            id="last__active"
            value={formatBackendDate(user.loginDate, NO_DATE)}
            editable={false}
            style={{ gridColumn: '1/2' }}
          />

          <DropDownInput
            items={dropdownStatus as DropdownItemStatus[]}
            onSelect={handleSelectStatus}
            initialValue={selectedStatus}
            editable={editable}
            style={{ gridColumn: '1/2' }}
          />
        </div>
      </div>
    </div>
  );
}

export default InfoCardSmall;
